import { Injectable } from "@angular/core";
import Graphic from "@arcgis/core/Graphic";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import EsriSymbol from "@arcgis/core/symbols/Symbol";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IMapGeometry, IMapIcon, SymbolStyle } from "../map.settings";
import { Symbols } from "../symbols/symbol.styles";

export interface ISymbolParameters {
    type?: string;
    colors?: any[];
    iconid?: string;
    size?: number;
    style?: SymbolStyle;
    selected?: boolean;
    selectedColor?: any;
}

@Injectable({
    providedIn: "root"
})
export class SymbolService {

    getSymbolByStyleName(styleName: string, isSelected = false) {
        const symbol = this.getCopyOfSymbol(Symbols.Styles[styleName]);
        if (isSelected) {
            this.blendSymbols(symbol, this.getSelectedSymbol(symbol));
        }
        return symbol;
    }

    getSymbol(params: ISymbolParameters, predefSymbols?: any[], icons?: IMapIcon[]): EsriSymbol {

        if (params.iconid && icons) {
            return this.fromIcon(icons.find(x => x.id === params.iconid), params.selected);
        }

        if (params.iconid && predefSymbols && predefSymbols.length) {
            return predefSymbols.find(x => x.attributes.id === params.iconid);
        }

        switch (params.type) {
            case "point":
                return new SimpleMarkerSymbol({
                    color: this.getColorOrDefault(params.colors),
                    size: this.getSizeOrDefault(10, params.size),
                    style: !ObjectUtils.isNullOrUndefined(params.style) ? params.style : "circle",
                    outline: params.selected ? this.getSelectedOutline(params.selectedColor) : this.getDefaultOutline(0.5, "black")
                });
            case "polyline":
                return new SimpleLineSymbol({
                    color: this.getColorOrDefault(params.colors),
                    width: this.getSizeOrDefault(4, params.size),
                    style: "solid",
                    cap: "round",
                    join: "round"
                });
            default:
                return new SimpleFillSymbol({
                    color: this.getColorOrDefault(params.colors),
                    outline: params.selected ? this.getSelectedOutline(params.selectedColor) : this.getDefaultOutline(2, "black"),
                    style: "solid"
                });
        }
    }

    setSymbol(graphic: Graphic, symbol: EsriSymbol) {
        graphic.set("symbol", symbol);
    }

    fromSymbolText(geom: IMapGeometry) {
        return {
            type: "text",
            color: geom.color,
            haloColor: geom.symbolHaloColor ?? null,
            haloSize: (geom.symbolHaloColor) ? geom.symbolHaloSize ?? "1px" : null,
            text: geom.symbolText ?? "SymbolText",
            xoffset: geom.xoffset ?? 0,
            yoffset: geom.yoffset ?? 0,
            font: {
                family: "Noto Sans",
                size: geom.size,
                style: "normal",
                weight: "normal"
            }
        };
    }

    cloneSymbol(symbol: any) {
        const newsymbol = {
            type: symbol.type,
            color: symbol.color,
            size: symbol.size,
            style: symbol.style,
            outline: symbol?.outline,
            text: symbol?.text,
            font: symbol?.font,
            xoffset: symbol?.xoffset,
            yoffset: symbol?.yoffset,
            cap: symbol?.cap,
            join: symbol?.join
        } as any;
        if (symbol.path) {
            newsymbol.path = symbol.path;
        }
        if (symbol.url) {
            newsymbol.url = symbol.url;
        }
        if (symbol.attributes) {
            newsymbol.attributes = symbol.attributes;
        }
        return newsymbol;
    }

    fromIcon(icon: IMapIcon, selected = false): EsriSymbol {

        let symbol: EsriSymbol;

        if (!icon) {
            return null;
        }
        if (ObjectUtils.hasValue(icon.arcgisStyle)) {
            symbol = this.fromIconToSimpleMarker(icon, selected);
        } else {
            symbol = this.fromIconToPictureMarker(icon, selected);
        }
        (symbol as any).attributes = { id: icon.id,
            selected: selected };

        return symbol;
    }

    fromLabel(geom: IMapGeometry) {
        return {
            type: "text",
            color: "white",
            haloColor: "black",
            haloSize: "1px",
            text: geom.label ? geom.label : "Label",
            xoffset: 3,
            yoffset: 3
        };
    }

    private fromIconToSimpleMarker(icon: IMapIcon, selected: boolean): SimpleMarkerSymbol {
        return new SimpleMarkerSymbol({
            color: icon.color ? icon.color : "black",
            outline: selected ? this.getSelectedOutline("cyan") : this.getDefaultOutline(icon.outlineWidth, icon.outlineColor),
            path: icon?.iconSvgPath,
            xoffset: icon?.xoffset,
            yoffset: icon?.yoffset,
            style: icon.arcgisStyle,
            size: icon.size
        });
    }

    private fromIconToPictureMarker(icon: IMapIcon, selected: boolean): PictureMarkerSymbol {
        const imageUrl = selected && icon.iconUrlPathSelected ? icon.iconUrlPathSelected : icon.iconUrlPath;
        return new PictureMarkerSymbol({
            color: icon.color ? icon.color : "black",
            xoffset: icon?.xoffset,
            yoffset: icon?.yoffset,
            url: imageUrl ? imageUrl : null,
            height: icon.iconUrlPath ? icon.height : null,
            width: icon.iconUrlPath ? icon.width : null
        });
    }

    private blendSymbols(baseSymbol, addedSymbol) {
        const result = {};
        this.applyAllSettings(baseSymbol, result);
        this.applyAllSettings(addedSymbol, result);
        return result;
    }

    private getCopyOfSymbol(symbol) {
        const result = {};
        return this.applyAllSettings(symbol, result);
    }

    private applyAllSettings(source, target) {
        for (const setting in source) {
            if (Object.prototype.hasOwnProperty.call) {
                const sourceItem = source[setting];
                if (!this.isSettingObject(sourceItem)) {
                    target[setting] = sourceItem;
                }
                if (this.isSettingObject(sourceItem)) {
                    const targetItem = target[setting] ? target[setting] : {};
                    target[setting] = this.applyAllSettings(sourceItem, targetItem);
                }
            }
        }
        return target;
    }
    private isSettingObject(obj) {
        return !Array.isArray(obj) && typeof obj === "object" && obj !== null;
    }

    private getColorOrDefault(colors?: any[]): any {
        const spec = colors && colors.length > 0 ? colors.filter(x => x != null) : null;
        return spec != null && spec.length > 0 ? spec[0] : [
            255,
            255,
            255,
            1
        ];
    }

    private getSizeOrDefault(def: number, size?: number) {
        return size != null ? size : def;
    }

    private getDefaultOutline(width?: number, color?: any): any {
        if (width || color) {
            return {
                type: "simple-line",
                color: color ?? color.black,
                width: width ?? 0.5,
                cap: "round",
                join: "round"
            };
        }
        return Symbols.Styles.default;
    }

    private getSelectedSymbol(targetSymbol) {
        const empty = {};
        switch (targetSymbol.type) {
            case "simple-marker": return Symbols.Styles.defaultSelected;
            case "simple-line": return empty;
            default: return Symbols.Styles.defaultSelected;
        }
    }

    private getSelectedOutline(color: any): SimpleLineSymbol {
        return new SimpleLineSymbol({
            color: color,
            width: 0.75,
            style: "solid"
        });
    }

}
